import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Card, CardContent,
    List, ListItem, ListItemText, Chip, CircularProgress, Alert,
    Button, Avatar, useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Cake as CakeIcon, Dashboard as DashboardIcon, Gavel as GavelIcon, Event as EventIcon } from '@mui/icons-material';
import api from '../services/api';

function Dashboard() {
    const navigate = useNavigate();
    const theme = useTheme();
    const [ultimosProcessos, setUltimosProcessos] = useState([]);
    const [aniversariantes, setAniversariantes] = useState([]);
    const [acessosRecentes, setAcessosRecentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAniversariantes, setLoadingAniversariantes] = useState(true);
    const [loadingAcessos, setLoadingAcessos] = useState(true);
    const [error, setError] = useState('');

    // Função para obter cor de fundo adaptável ao tema
    const getBackgroundColor = (tipo) => {
        if (theme.palette.mode === 'dark') {
            // Cores escuras para tema escuro
            return tipo === 'PROCESSO' 
                ? 'rgba(25, 118, 210, 0.15)' // Azul translúcido escuro
                : 'rgba(156, 39, 176, 0.15)'; // Roxo translúcido escuro
        } else {
            // Cores claras para tema claro
            return tipo === 'PROCESSO' 
                ? '#e3f2fd' // Azul muito claro
                : '#f3e5f5'; // Roxo muito claro
        }
    };

    // Função para obter cor de hover adaptável ao tema
    const getHoverColor = (tipo) => {
        if (theme.palette.mode === 'dark') {
            return tipo === 'PROCESSO'
                ? 'rgba(25, 118, 210, 0.25)' // Azul translúcido mais escuro
                : 'rgba(156, 39, 176, 0.25)'; // Roxo translúcido mais escuro
        } else {
            return tipo === 'PROCESSO'
                ? '#bbdefb' // Azul claro
                : '#e1bee7'; // Roxo claro
        }
    };

    useEffect(() => {
        loadUltimosProcessos();
        loadAniversariantes();
        loadAcessosRecentes();
    }, []);

    const loadUltimosProcessos = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/auditoria/ultimos-processos?limit=15');
            setUltimosProcessos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar últimos processos:', error);
            // Se não for admin, não mostra erro, apenas não exibe a lista
            if (error.response?.status === 403) {
                setError('Você não tem permissão para ver esta informação');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadAniversariantes = async () => {
        setLoadingAniversariantes(true);
        try {
            const response = await api.get('/clientes/aniversariantes?dias_proximos=30');
            setAniversariantes(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar aniversariantes:', error);
        } finally {
            setLoadingAniversariantes(false);
        }
    };

    const loadAcessosRecentes = async () => {
        setLoadingAcessos(true);
        try {
            const response = await api.get('/auditoria/acessos-hoje?limit=15');
            setAcessosRecentes(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar acessos recentes:', error);
        } finally {
            setLoadingAcessos(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Se a data já vem formatada do backend (YYYY-MM-DD HH:mm:ss), formata diretamente
            // O backend agora retorna em UTC, então precisamos tratar como UTC
            if (typeof date === 'string') {
                // Formato: "2026-01-02 21:54:31" (UTC)
                const match = date.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                if (match) {
                    const [, ano, mes, dia, hora, minuto, segundo] = match;
                    // Cria uma data em UTC e converte para o timezone local (America/Sao_Paulo)
                    const dataUTC = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}Z`);
                    // Formata para o timezone local
                    const diaLocal = String(dataUTC.getDate()).padStart(2, '0');
                    const mesLocal = String(dataUTC.getMonth() + 1).padStart(2, '0');
                    const anoLocal = dataUTC.getFullYear();
                    const horaLocal = String(dataUTC.getHours()).padStart(2, '0');
                    const minutoLocal = String(dataUTC.getMinutes()).padStart(2, '0');
                    const segundoLocal = String(dataUTC.getSeconds()).padStart(2, '0');
                    return `${diaLocal}/${mesLocal}/${anoLocal}, ${horaLocal}:${minutoLocal}:${segundoLocal}`;
                }
                // Se já está em formato brasileiro, retorna como está
                if (date.includes('/')) {
                    return date;
                }
            }
            
            // Fallback: tenta converter (assume UTC se não tiver timezone)
            const d = new Date(date);
            if (isNaN(d.getTime())) {
                return date;
            }
            
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const ano = d.getFullYear();
            const hora = String(d.getHours()).padStart(2, '0');
            const minuto = String(d.getMinutes()).padStart(2, '0');
            const segundo = String(d.getSeconds()).padStart(2, '0');
            
            return `${dia}/${mes}/${ano}, ${hora}:${minuto}:${segundo}`;
        } catch {
            return date;
        }
    };

    const formatTime = (time, created_at) => {
        // Sempre usa created_at para garantir conversão correta de UTC para local
        if (created_at) {
            try {
                // created_at vem como "YYYY-MM-DD HH:MM:SS" (UTC)
                const match = created_at.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                if (match) {
                    const [, ano, mes, dia, hora, minuto] = match;
                    // Cria uma data em UTC e converte para o timezone local
                    const dataUTC = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:00Z`);
                    // Formata para o timezone local
                    const horaLocal = String(dataUTC.getHours()).padStart(2, '0');
                    const minutoLocal = String(dataUTC.getMinutes()).padStart(2, '0');
                    return `${horaLocal}:${minutoLocal}`;
                }
            } catch (e) {
                console.error('Erro ao formatar hora:', e);
            }
        }
        
        // Fallback: se não tiver created_at, usa o time fornecido
        if (time && time.includes(':')) {
            return time.substring(0, 5);
        }
        
        return time || '-';
    };


    const formatAniversario = (dia, mes) => {
        const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        return `${dia} de ${meses[mes - 1]}`;
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <DashboardIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Dashboard
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Visão geral do sistema jurídico
                </Typography>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 2 }}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" gutterBottom>
                            Últimos Processos Acessados
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="info">{error}</Alert>
                        ) : ultimosProcessos.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Nenhum processo acessado recentemente.
                            </Typography>
                        ) : (
                            <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                                {ultimosProcessos.map((processo) => (
                                    <ListItem
                                        key={processo.processo_id}
                                        sx={{
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                        onClick={() => navigate(`/processos/editar/${processo.processo_id}`)}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography
                                                        component="button"
                                                        type="button"
                                                        variant="body1"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/processos/editar/${processo.processo_id}`);
                                                        }}
                                                        sx={{ 
                                                            textDecoration: 'none', 
                                                            fontWeight: 'bold', 
                                                            border: 'none', 
                                                            background: 'none', 
                                                            padding: 0, 
                                                            cursor: 'pointer',
                                                            color: 'inherit',
                                                            fontFamily: 'inherit',
                                                            fontSize: 'inherit',
                                                            lineHeight: 'inherit'
                                                        }}
                                                    >
                                                        {processo.numero_processo || `ID: ${processo.processo_id}`}
                                                    </Typography>
                                                    {processo.status && (
                                                        <Chip label={processo.status} size="small" />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box component="span" sx={{ display: 'block' }}>
                                                    <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                                        Cliente: {processo.cliente_nome || 'Não informado'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                                        Último acesso: {formatDate(processo.ultimo_acesso)}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondaryTypographyProps={{ component: 'div' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Aniversariantes
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => navigate('/aniversariantes')}
                                        >
                                            Ver todos
                                        </Button>
                                    </Box>
                                    {loadingAniversariantes ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : aniversariantes.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhum aniversário nos próximos 30 dias.
                                        </Typography>
                                    ) : (
                                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                            {aniversariantes.slice(0, 10).map((aniversariante) => (
                                                <ListItem
                                                    key={aniversariante.id}
                                                    sx={{
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: 'action.hover' }
                                                    }}
                                                    onClick={() => navigate(`/clientes/editar/${aniversariante.id}`)}
                                                >
                                                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                                                        <CakeIcon />
                                                    </Avatar>
                                                    <ListItemText
                                                        primary={aniversariante.nome}
                                                        secondary={
                                                            <Box component="span" sx={{ display: 'block' }}>
                                                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography variant="body2" color="text.secondary" component="span">
                                                                        {formatAniversario(aniversariante.dia_aniversario, aniversariante.mes_aniversario)}
                                                                    </Typography>
                                                                    {aniversariante.dias_ate_aniversario === 0 && (
                                                                        <Chip label="Hoje!" size="small" color="error" sx={{ ml: 1, display: 'inline-flex' }} />
                                                                    )}
                                                                    {aniversariante.dias_ate_aniversario > 0 && aniversariante.dias_ate_aniversario <= 7 && (
                                                                        <Chip label={`Em ${aniversariante.dias_ate_aniversario} dias`} size="small" color="warning" sx={{ ml: 1, display: 'inline-flex' }} />
                                                                    )}
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
                                                                    {aniversariante.idade_atual} anos (faz {aniversariante.idade_proxima} anos)
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        secondaryTypographyProps={{ component: 'div' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Acessos Recentes (Hoje)
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() => navigate('/historico-acesso')}
                                        >
                                            Ver todos
                                        </Button>
                                    </Box>
                                    {loadingAcessos ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : acessosRecentes.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhum acesso registrado hoje.
                                        </Typography>
                                    ) : (
                                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                            {acessosRecentes.slice(0, 10).map((acesso) => (
                                                <ListItem
                                                    key={acesso.id}
                                                    sx={{
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        cursor: 'pointer',
                                                        backgroundColor: getBackgroundColor(acesso.tipo_registro),
                                                        '&:hover': { 
                                                            bgcolor: getHoverColor(acesso.tipo_registro)
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        if (acesso.tipo_registro === 'PROCESSO') {
                                                            navigate(`/processos/ver/${acesso.registro_id}`);
                                                        } else {
                                                            navigate(`/prazos/editar/${acesso.registro_id}`);
                                                        }
                                                    }}
                                                >
                                                    <Avatar 
                                                        sx={{ 
                                                            bgcolor: acesso.tipo_registro === 'PROCESSO' ? '#1976d2' : '#42a5f5',
                                                            mr: 2,
                                                            width: 32,
                                                            height: 32
                                                        }}
                                                    >
                                                        {acesso.tipo_registro === 'PROCESSO' ? <GavelIcon sx={{ fontSize: 18 }} /> : <EventIcon sx={{ fontSize: 18 }} />}
                                                    </Avatar>
                                                    <ListItemText
                                                        primary={
                                                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Typography variant="body2" fontWeight="medium" component="span">
                                                                    {acesso.registro_descricao || `#${acesso.registro_id}`}
                                                                </Typography>
                                                                <Chip 
                                                                    label={acesso.tipo_registro} 
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: acesso.tipo_registro === 'PROCESSO' ? '#1976d2' : '#42a5f5',
                                                                        color: 'white',
                                                                        fontSize: '0.7rem',
                                                                        height: '20px'
                                                                    }}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Typography variant="caption" color="text.secondary" component="span">
                                                                    {formatTime(acesso.hora_acesso, acesso.created_at)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" component="span">
                                                                    • {acesso.usuario_nome || 'Usuário'}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                        secondaryTypographyProps={{ component: 'div' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Dashboard;
